import React, {useEffect, useRef, useState} from 'react';
import {Alert, Button, Form, Modal, Table} from 'react-bootstrap';
import {BsArchive, BsGripVertical, BsPlusLg, BsTrash} from 'react-icons/bs';
import {Timestamp} from 'firebase/firestore';
import {IFirestoreProductionResource, IProductionZone} from '../data/interfaces';
import {createProductionResourceId, getActiveProductionResources, isProductionResourceArchived} from '../data/productionResources';

interface ProductionResourceManagerProps {
  show: boolean;
  resources: IFirestoreProductionResource[];
  selectedProductionZone: IProductionZone;
  onClose: () => void;
  onSave: (resources: IFirestoreProductionResource[]) => void;
}

function moveResource(
  resources: IFirestoreProductionResource[],
  fromIndex: number,
  toIndex: number
): IFirestoreProductionResource[] {
  const nextResources = [...resources];
  const [movedResource] = nextResources.splice(fromIndex, 1);
  nextResources.splice(toIndex, 0, movedResource);

  return nextResources;
}

function moveActiveResource(
  resources: IFirestoreProductionResource[],
  fromIndex: number,
  toIndex: number
): IFirestoreProductionResource[] {
  const movedActiveResources = moveResource(getActiveProductionResources(resources), fromIndex, toIndex);
  let activeResourceIndex = 0;

  return resources.map((resource) => {
    if (isProductionResourceArchived(resource)) {
      return resource;
    }

    const movedActiveResource = movedActiveResources[activeResourceIndex];
    activeResourceIndex += 1;
    return movedActiveResource;
  });
}

function normalizeResourceNameForComparison(name: string): string {
  return name.toLowerCase();
}

function normalizeResourceNameForSave(name: string): string {
  return name.trim().replace(/\s+/g, ' ');
}

function getResourceValidationError(
  resources: IFirestoreProductionResource[]
): string | null {
  const resourceNames = resources.map((resource) =>
    normalizeResourceNameForSave(resource.name)
  );

  if (resourceNames.some((resourceName) => !resourceName)) {
    return 'Resource names cannot be blank.';
  }

  const normalizedResourceNames = resourceNames.map(normalizeResourceNameForComparison);
  const duplicateResourceIndex = normalizedResourceNames.findIndex((resourceName, index) =>
    normalizedResourceNames.indexOf(resourceName) !== index
  );

  return duplicateResourceIndex >= 0
    ? `Resource name ${resourceNames[duplicateResourceIndex]} already exists.`
    : null;
}

const ProductionResourceManager: React.FC<ProductionResourceManagerProps> = ({
  show,
  resources,
  selectedProductionZone,
  onClose,
  onSave,
}) => {
  const [draftResources, setDraftResources] = useState<IFirestoreProductionResource[]>([]);
  const [draggedResourceIndex, setDraggedResourceIndex] = useState<number | null>(null);
  const resourceNameInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const activeDraftResources = getActiveProductionResources(draftResources);
  const validationError = getResourceValidationError(activeDraftResources);

  useEffect(() => {
    if (!show) {
      return;
    }

    setDraftResources(resources);
  }, [resources, show]);

  const handleResourceNameChange = (resourceId: string, name: string) => {
    setDraftResources((currentResources) =>
      currentResources.map((resource) =>
        resource.id === resourceId
          ? {...resource, name}
          : resource
      )
    );
  };

  const handleResourceNameKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    resourceIndex: number
  ) => {
    if (event.key !== 'Tab' || event.shiftKey) {
      return;
    }

    const nextResourceNameInput = resourceNameInputRefs.current[resourceIndex + 1];
    if (!nextResourceNameInput) {
      return;
    }

    event.preventDefault();
    nextResourceNameInput.focus();
  };

  const handleAddResource = () => {
    const newResource: IFirestoreProductionResource = {
      id: createProductionResourceId(),
      name: '',
      createdAt: Timestamp.now(),
      archivedAt: null,
      productionZone: selectedProductionZone.ref,
      sortOrder: activeDraftResources.length + 1,
    };

    const newResourceInputIndex = activeDraftResources.length;
    setDraftResources((currentResources) => [...currentResources, newResource]);
    setTimeout(() => resourceNameInputRefs.current[newResourceInputIndex]?.focus(), 0);
  };

  const handleDeleteDraftResource = (resourceId: string) => {
    setDraftResources((currentResources) =>
      currentResources.filter((resource) => resource.id !== resourceId)
    );
  };

  const handleDropResource = (toIndex: number) => {
    if (draggedResourceIndex === null) {
      return;
    }

    setDraftResources((currentResources) =>
      moveActiveResource(currentResources, draggedResourceIndex, toIndex)
    );
  };

  const handleArchiveResource = (resourceId: string) => {
    if (!window.confirm('Archive this resource? Current and future events assigned to it will appear under Unassigned.')) {
      return;
    }

    setDraftResources((currentResources) =>
      currentResources.map((resource) =>
        resource.id === resourceId
          ? {...resource, archivedAt: Timestamp.now()}
          : resource
      )
    );
  };

  const isSavedResource = (resourceId: string) =>
    resources.some((resource) => resource.id === resourceId);

  const handleSave = () => {
    const resourcesToSave = draftResources.map((resource) =>
      isProductionResourceArchived(resource)
        ? resource
        : {
          ...resource,
          name: normalizeResourceNameForSave(resource.name),
          productionZone: selectedProductionZone.ref,
        }
    );
    const activeResourcesToSave = getActiveProductionResources(resourcesToSave);
    const saveValidationError = getResourceValidationError(activeResourcesToSave);

    if (saveValidationError) {
      return;
    }

    onSave(resourcesToSave);
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} className="event-modal production-resource-manager">
      <Modal.Header closeButton>
        <Modal.Title>
          Manage Resources - {selectedProductionZone.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Table responsive className="align-middle mb-3">
          <thead>
          <tr>
            <th className="production-resource-drag-column" aria-label="Sort order"></th>
            <th>Resource Name</th>
            <th className="text-end">Action</th>
          </tr>
          </thead>
          <tbody>
          {activeDraftResources.map((resource, index) => (
            <tr
              key={resource.id}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => handleDropResource(index)}
            >
              <td className="production-resource-drag-column">
                <Button
                  type="button"
                  variant="link"
                  className="production-resource-drag-handle"
                  draggable
                  onDragStart={() => setDraggedResourceIndex(index)}
                  onDragEnd={() => setDraggedResourceIndex(null)}
                  aria-label="drag resource"
                  title="drag resource"
                >
                  <BsGripVertical/>
                </Button>
              </td>
              <td>
                <Form.Control
                  className="production-resource-name-input"
                  type="text"
                  value={resource.name}
                  ref={(element: HTMLInputElement | null) => {
                    resourceNameInputRefs.current[index] = element;
                  }}
                  onChange={(event) => handleResourceNameChange(resource.id, event.target.value)}
                  onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleResourceNameKeyDown(event, index)}
                  aria-label="Resource name"
                />
              </td>
              <td className="text-end">
                {isSavedResource(resource.id) ? (
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleArchiveResource(resource.id)}
                    aria-label="archive resource"
                    title="archive resource"
                  >
                    <BsArchive/>
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteDraftResource(resource.id)}
                    aria-label="delete resource"
                    title="delete resource"
                  >
                    <BsTrash/>
                  </Button>
                )}
              </td>
            </tr>
          ))}
          </tbody>
        </Table>

        {validationError && (
          <Alert variant="danger">
            {validationError}
          </Alert>
        )}

        <Button
          type="button"
          variant="outline-primary"
          onClick={handleAddResource}
        >
          <BsPlusLg className="me-1"/>
          Add Resource
        </Button>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          variant="outline-primary"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSave}
          disabled={validationError !== null}
        >
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductionResourceManager;
